import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { shareInflightRequest } from '../../utils/share-inflight-request.util';
import { IProcessingResultsService } from '../../interfaces/services/processing-results-service.interface';
import {
  ProcessingResult,
  ProcessingResultsPage,
  ProcessingResultsQuery,
} from '../../interfaces/state/entities/processing-result.interface';
import {
  CaptureProcessingResultDetail,
  DrawingProcessingResultDetail,
} from '../../interfaces/state/entities/processing-result-detail.interface';
import { environment } from '../../environments/environment';
import { RegisteredApplicationStore } from '../registered-applications/registered-application.store';

@Injectable({
  providedIn: 'root',
})
export class ProcessingResultsService implements IProcessingResultsService {
  private http = inject(HttpClient);
  private registeredApplicationStore = inject(RegisteredApplicationStore);
  private readonly apiUrl = `${environment.MayaTestToolApiBaseUrl}/ProcessingResults`;
  private readonly inflightDrawingDetails = new Map<
    string,
    Observable<DrawingProcessingResultDetail>
  >();
  private readonly inflightCaptureDetails = new Map<
    string,
    Observable<CaptureProcessingResultDetail>
  >();

  getProcessingResults(query: ProcessingResultsQuery): Observable<ProcessingResultsPage> {
    let params = new HttpParams().set('ProjectId', query.projectId);

    if (query.startAt?.trim()) {
      params = params.set('StartAt', query.startAt.trim());
    }
    if (query.direction?.trim()) {
      params = params.set('Direction', query.direction.trim());
    }
    if (query.bookmark?.trim()) {
      params = params.set('Bookmark', query.bookmark.trim());
    }

    const headers = this.buildHeaders();

    return this.http.get<{ items?: any[]; continuation?: any }>(this.apiUrl, { params, headers }).pipe(
      map((response) => ({
        items: (response.items ?? []).map((item) => this.mapItem(item)),
        continuation: {
          hasMoreData: response.continuation?.hasMoreData ?? false,
          bookmark: response.continuation?.bookmark ?? response.continuation?.Bookmark,
        },
      }))
    );
  }

  private mapItem(item: any): ProcessingResult {
    return {
      processId: item.processId,
      createdAt: new Date(item.createdAt),
      processType: item.processType,
      status: item.status,
      processedAt: item.processedAt ? new Date(item.processedAt) : null,
    };
  }

  getDrawingProcessingResult(processId: string): Observable<DrawingProcessingResultDetail> {
    const key = `${this.clientKey()}:${processId}`;
    return shareInflightRequest(this.inflightDrawingDetails, key, () => {
      const params = new HttpParams().set('ProcessId', processId);
      return this.http
        .get<DrawingProcessingResultDetail>(`${this.apiUrl}/Drawings`, {
          params,
          headers: this.buildHeaders(),
        })
        .pipe(map((response) => this.mapDrawingDetail(response)));
    });
  }

  getCaptureProcessingResult(processId: string): Observable<CaptureProcessingResultDetail> {
    const key = `${this.clientKey()}:${processId}`;
    return shareInflightRequest(this.inflightCaptureDetails, key, () => {
      const params = new HttpParams().set('ProcessId', processId);
      return this.http
        .get<CaptureProcessingResultDetail>(`${this.apiUrl}/Captures`, {
          params,
          headers: this.buildHeaders(),
        })
        .pipe(map((response) => this.mapCaptureDetail(response)));
    });
  }

  private mapCaptureDetail(
    response: CaptureProcessingResultDetail | null
  ): CaptureProcessingResultDetail {
    if (!response) {
      return {};
    }
    return {
      extraction: response.extraction,
      processing: response.processing,
      output: response.output,
    };
  }

  private buildHeaders(): HttpHeaders | undefined {
    const clientId = this.registeredApplicationStore.selectedId();
    return clientId !== null && clientId.trim() !== ''
      ? new HttpHeaders({ 'x-ClientId': clientId })
      : undefined;
  }

  private clientKey(): string {
    return this.registeredApplicationStore.selectedId() ?? '';
  }

  private mapDrawingDetail(response: DrawingProcessingResultDetail): DrawingProcessingResultDetail {
    return {
      extraction: response.extraction,
      processing: response.processing,
      output: response.output,
    };
  }
}
